import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Pill, Heartbeat, CalendarBlank, Clock, Eye, Warning,
  CheckCircle, ArrowRight, ArrowLeft, Notebook, ChatCircle,
  FileText, Sun, Lightning, Target, X, Play, Pause, ArrowClockwise,
  CaretRight, Bed, Brain, Stethoscope, ListChecks, File,
  PaperPlaneRight, Clipboard, UserCircle, Sparkle
} from '@phosphor-icons/react';
import { track } from '../services/activation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Auto-advance pacing: 8 steps × ~3.75s ≈ 30s total
const AUTO_ADVANCE_MS = 3750;

// Demo account credentials (seeded investor account)
const DEMO_ACCOUNT = {
  email: 'johndoe@kindrahealth.app',
  password: 'Taleeia2024!',
};

/* ═══════════════════════════════════════════════════
   MASTER DEMO PROFILE — Immutable. Second-person framing.
   ═══════════════════════════════════════════════════ */
const P = {
  name: 'Sarah',
  conditions: [
    { name: 'Chronic Fatigue Syndrome', severity: 'moderate', since: 'June 2023', diagnosedBy: 'Dr. James Liu, Greenslopes Medical' },
    { name: 'Generalised Anxiety Disorder', severity: 'mild', since: 'Jan 2022', diagnosedBy: 'Dr. James Liu, Greenslopes Medical' },
    { name: 'Insomnia', severity: 'moderate', since: 'Sept 2023', diagnosedBy: 'Dr. Anika Patel, Brisbane Sleep & Respiratory Centre' },
  ],
  medications: [
    { name: 'Sertraline', dose: '50mg', when: 'Evening', purpose: 'Anxiety management', prescriber: 'Dr. James Liu' },
    { name: 'Melatonin', dose: '3mg', when: 'Nightly', purpose: 'Sleep onset', prescriber: 'Dr. Anika Patel' },
    { name: 'Magnesium Glycinate', dose: '400mg', when: 'Nightly', purpose: 'Sleep quality', prescriber: 'Self-managed (discussed with GP)' },
  ],
  symptoms: [
    { name: 'Persistent fatigue', freq: 'Daily', score: 7 },
    { name: 'Brain fog', freq: 'Most days', score: 6 },
    { name: 'Muscle pain', freq: 'Several times/week', score: 4 },
    { name: 'Poor sleep quality', freq: 'Daily', score: 8 },
  ],
  checkins: [
    { day: 'Today', mood: 4, sleep: 4.5, energy: 3, anxiety: 7, note: 'Woke at 3am again, couldn\'t fall back asleep' },
    { day: 'Yesterday', mood: 5, sleep: 5.2, energy: 4, anxiety: 5, note: 'Slightly better after magnesium before bed' },
    { day: '2 days ago', mood: 3, sleep: 3.5, energy: 2, anxiety: 8, note: 'Worst night in weeks, very anxious before bed' },
    { day: '3 days ago', mood: 5, sleep: 6.0, energy: 5, anxiety: 4, note: 'Wind-down routine worked, slept until 5am' },
    { day: '4 days ago', mood: 4, sleep: 4.0, energy: 3, anxiety: 6, note: 'Sunday night restlessness' },
  ],
  appointments: {
    upcoming: { doctor: 'Dr. Anika Patel', role: 'Sleep & Fatigue Specialist', date: 'April 28, 2025', time: '10:30am', location: 'Brisbane Sleep & Respiratory Centre, Spring Hill' },
    past: { doctor: 'Dr. James Liu', role: 'General Practitioner', date: 'March 15, 2025', location: 'Greenslopes Medical Centre', notes: 'Reviewed fatigue progression. Discussed Sertraline effectiveness. Referred to Dr. Patel for sleep-specific management.' },
    referral: { from: 'Dr. James Liu', to: 'Dr. Anika Patel', date: 'March 15, 2025', reason: 'Persistent insomnia with chronic fatigue not responding to first-line intervention. Sleep maintenance issues despite melatonin. Requesting specialist assessment and consideration of CBT-I or alternative sleep management.' },
  },
  documents: [
    {
      title: 'Full Blood Examination & Iron Studies', type: 'Pathology Report',
      source: 'QML Pathology — Greenslopes Collection Centre',
      date: 'March 12, 2025', orderedBy: 'Dr. James Liu, Greenslopes Medical Centre',
      ref: 'QML-2025-0312-4478',
      summary: 'Your iron levels are borderline low and your Vitamin D is at the lower end of normal. Thyroid function is fine. These results are consistent with your fatigue symptoms and have been shared with Dr. Patel ahead of your specialist appointment.',
      findings: [
        { label: 'Ferritin', value: '18 ng/mL', range: '20–200 ng/mL', flag: 'Borderline low — may contribute to fatigue' },
        { label: 'Vitamin D (25-OH)', value: '42 nmol/L', range: '50–250 nmol/L', flag: 'Below optimal — supplementation may help' },
        { label: 'TSH', value: '2.1 mIU/L', range: '0.4–4.0 mIU/L', flag: 'Normal — thyroid is not the issue' },
        { label: 'Vitamin B12', value: '380 pmol/L', range: '150–750 pmol/L', flag: 'Adequate — no action needed' },
      ],
    },
    {
      title: 'Polysomnography Report (Overnight Sleep Study)', type: 'Specialist Investigation',
      source: 'Brisbane Sleep & Respiratory Centre, Spring Hill',
      date: 'January 18, 2025', orderedBy: 'Dr. Anika Patel',
      ref: 'BSRC-PSG-2025-0118',
      summary: 'Your sleep architecture is fragmented with frequent micro-arousals. Sleep efficiency is 68%, well below the healthy threshold of 85%. No significant sleep apnoea detected. This confirms chronic insomnia with a sleep maintenance pattern — consistent with anxiety-driven arousal.',
      findings: [
        { label: 'Sleep efficiency', value: '68%', range: '>85%', flag: 'Below threshold — confirms insomnia' },
        { label: 'Micro-arousals', value: '14/hour', range: '<5/hour', flag: 'Elevated — explains unrefreshing sleep' },
        { label: 'Apnoea-Hypopnoea Index', value: '2.1/hour', range: '<5/hour', flag: 'Normal — sleep apnoea ruled out' },
        { label: 'REM latency', value: '142 min', range: '70–120 min', flag: 'Prolonged — may relate to SSRI timing' },
      ],
    },
  ],
  doctorBrief: {
    appointment: 'Dr. Anika Patel — Brisbane Sleep & Respiratory Centre — April 28, 2025',
    concerns: ['Sleep quality has declined significantly over past 2 weeks', 'Fatigue worsening — energy averaging 3.2/10', 'Anxiety increasing (4/10 → 6-7/10)'],
    changes: ['Sleep duration down from 5.5h to 4.8h average', 'Night waking frequency increased to 4-5 times/week', 'Brain fog affecting work performance on 3 of last 5 days'],
    medications: ['Sertraline 50mg (evening) — discuss timing change to morning', 'Melatonin 3mg — discuss dose optimisation', 'Magnesium 400mg — positive effect on sleep duration (+30min)'],
    questions: ['Should we switch Sertraline to morning dosing?', 'Is CBT-I (Cognitive Behavioural Therapy for Insomnia) appropriate?', 'Should melatonin dose be adjusted (evidence for lower doses)?', 'Would iron supplementation help given borderline ferritin (18 ng/mL)?'],
    keyData: { sleepAvg: '4.8h', energyAvg: '3.2/10', anxietyAvg: '6.2/10', wakings: '4-5/week' },
    referralContext: 'Referred by Dr. James Liu (Greenslopes Medical) on March 15 for persistent insomnia with chronic fatigue not responding to first-line intervention.',
  },
  course: {
    title: 'Understanding Your Fatigue',
    step: 2, total: 7,
    currentSection: { heading: 'What to watch for', content: 'Watch for energy crashes after 2pm — you\'ve logged this 4 times in the past 2 weeks. Brain fog that affects your work performance is another signal that your fatigue is compounding. And pay attention to the anxiety-fatigue spiral: when you worry about being tired, your sleep gets worse, which makes the fatigue worse.' },
  },
  signals: [
    { level: 'high', cat: 'Pattern', msg: 'Your sleep has declined 30% over 2 weeks. This is directly driving increased fatigue and anxiety.', action: 'Review your sleep patterns and raise this with Dr. Patel at your April 28 appointment.' },
    { level: 'moderate', cat: 'Medication', msg: 'Evening Sertraline may be contributing to your 2-3am wake-ups. Morning dosing could help.', action: 'Discuss timing change with Dr. Patel.' },
  ],
};

const PROMPTS = [
  'Why am I so tired lately?',
  'Are my medications making this worse?',
  'What should I focus on this week?',
  'What should I ask at my next appointment?',
  'Can I run a marathon?',
  'Tell me about my sleep patterns',
];

/* ═══════════════════════════════════════════════════
   GUIDED STEPS (8 total)
   ═══════════════════════════════════════════════════ */
const STEPS = [
  { id: 'profile', view: 'profile', title: 'Your Connected Health Profile', text: 'This is your connected health profile. Instead of everything being scattered across memory, documents, and appointments — Kindra keeps it all connected in one place.' },
  { id: 'brain', view: 'dashboard', title: 'What Matters Today', text: 'This is where Kindra helps you between appointments — showing what\'s changed, what matters, and what to do next.' },
  { id: 'course', view: 'course', title: 'Guided Next Steps', text: 'Kindra doesn\'t just show information — it helps guide what to do next in a calm, structured way.' },
  { id: 'chat', view: 'chat', title: 'The Kindra Experience', text: 'This is not a chatbot — it\'s responding based on your actual health data, patterns, medications, and appointments.' },
  { id: 'docs', view: 'documents', title: 'Document Intelligence', text: 'Kindra helps turn complex medical documents into something you can actually understand and use.' },
  { id: 'appt', view: 'appointments', title: 'Appointment Intelligence', text: 'This brings together everything that\'s happened between appointments, so you can walk in prepared.' },
  { id: 'brief', view: 'brief', title: 'Doctor Brief', text: 'Weeks of check-ins, symptoms, and medication changes — summarised clearly for your next conversation with your doctor.' },
  { id: 'close', view: 'dashboard', title: 'Care Between Appointments', text: 'Kindra gives you continuous support between appointments — helping you understand your health over time and act with clarity.' },
];

/* ═══════════════════════════════════════════════════
   GUIDE OVERLAY
   ═══════════════════════════════════════════════════ */
const Overlay = ({ step, onNext, onPrev, onSkip, autoPlay, paused, onTogglePause }) => {
  const s = STEPS[step];
  if (!s) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center pointer-events-none">
      <div className="absolute inset-0 bg-black/35 pointer-events-auto" onClick={onNext} />
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.25, delay: 0.1 }}
        className="relative z-10 bg-white rounded-2xl shadow-2xl mx-4 mb-6 sm:mb-0 p-5 max-w-md w-full pointer-events-auto" style={{ boxShadow: '0 25px 50px rgba(0,0,0,0.18)' }}
        data-testid="demo-guide-overlay">
        {/* Auto-advance progress bar */}
        {autoPlay && !paused && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-100 rounded-t-2xl overflow-hidden">
            <motion.div
              key={step}
              className="h-full bg-[#235347]"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
              data-testid="demo-autoplay-progress"
            />
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#235347]">Step {step + 1} of {STEPS.length}</span>
          <div className="flex items-center gap-3">
            <button
              onClick={onTogglePause}
              className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#235347] transition-colors min-h-[32px] px-1"
              data-testid="demo-autoplay-toggle"
              aria-label={paused ? 'Resume auto-advance' : 'Pause auto-advance'}
            >
              {paused ? <Play size={12} weight="fill" /> : <Pause size={12} weight="fill" />}
              <span>{paused ? 'Play' : 'Pause'}</span>
            </button>
            <button onClick={onSkip} className="text-[11px] text-slate-400 hover:text-slate-600" data-testid="demo-skip-guide">Skip tour</button>
          </div>
        </div>
        <h3 className="text-base font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="demo-guide-title">{s.title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-5">{s.text}</p>
        <div className="flex items-center justify-between">
          {step > 0 ? (
            <button onClick={onPrev} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 min-h-[40px] px-2" data-testid="demo-guide-prev"><ArrowLeft size={14} /> Back</button>
          ) : <div />}
          <button onClick={onNext} className="flex items-center gap-2 bg-[#235347] text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-[#1A3F36] min-h-[40px]" data-testid="demo-guide-next">
            {step === STEPS.length - 1 ? 'Finish Tour' : 'Next'} {step < STEPS.length - 1 && <ArrowRight size={14} />}
          </button>
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {STEPS.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? 'bg-[#235347]' : i < step ? 'bg-[#235347]/30' : 'bg-slate-200'}`} />)}
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════
   NAV
   ═══════════════════════════════════════════════════ */
const Nav = ({ active, onNav }) => {
  const items = [
    { id: 'dashboard', icon: Sun, label: 'Today' },
    { id: 'profile', icon: Heart, label: 'Health' },
    { id: 'course', icon: Notebook, label: 'Learn' },
    { id: 'chat', icon: ChatCircle, label: 'Chat' },
    { id: 'appointments', icon: CalendarBlank, label: 'Appts' },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-100 z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="demo-nav">
      <div className="max-w-lg mx-auto flex items-center justify-around px-2 py-1.5">
        {items.map(({ id, icon: Icon, label }) => (
          <button key={id} onClick={() => onNav(id)} className={`flex flex-col items-center gap-0.5 min-w-[52px] min-h-[46px] justify-center rounded-xl transition-colors ${active === id ? 'text-[#235347]' : 'text-slate-400'}`} data-testid={`demo-nav-${id}`}>
            <Icon size={20} weight={active === id ? 'fill' : 'regular'} />
            <span className="text-[10px] font-medium">{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

/* ═══════════════════════════════════════════════════
   VIEWS — All second-person, all self-contained
   ═══════════════════════════════════════════════════ */

/* ─── DASHBOARD ─── */
const DashboardView = ({ onNav }) => {
  const ci = P.checkins[0];
  return (
    <div className="space-y-5" data-testid="demo-dashboard">
      <div>
        <h1 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Good morning, {P.name}</h1>
        <p className="text-xs text-slate-400 mt-0.5">Here's what matters today</p>
      </div>
      {/* Daily Brain */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5" data-testid="demo-daily-brain">
        <p className="text-lg font-semibold text-amber-700 leading-snug mb-4">A few things to watch</p>
        <div className="bg-white/70 rounded-xl border border-white/80 p-4 mb-4">
          <p className="text-sm font-medium text-[#0F172A] mb-1.5">Your sleep has declined significantly</p>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">Averaging 4.8 hours over 14 days — your fatigue and anxiety are following the same pattern.</p>
          <button onClick={() => onNav('appointments')} className="inline-flex items-center gap-1.5 bg-[#235347] text-white rounded-lg px-4 py-2.5 text-xs font-medium min-h-[40px]" data-testid="demo-brain-cta">
            <Eye size={14} weight="bold" /> Review
          </button>
        </div>
        <div className="flex items-center gap-2 px-1 mb-2">
          <CalendarBlank size={13} className="text-slate-400" weight="duotone" />
          <p className="text-xs text-slate-400">Dr. Patel — April 28</p>
        </div>
        <div className="flex items-center gap-2 px-1">
          <Notebook size={13} className="text-slate-400" weight="duotone" />
          <p className="text-xs text-slate-400">Continue: Understanding Your Fatigue — Step 3 is relevant today</p>
        </div>
      </div>
      {/* Check-in snapshot */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Your check-in today</span>
        <div className="grid grid-cols-4 gap-3 mt-3">
          {[
            { label: 'Mood', val: ci.mood, max: 10, color: 'text-amber-500' },
            { label: 'Sleep', val: ci.sleep, max: null, suffix: 'h', color: 'text-blue-500' },
            { label: 'Energy', val: ci.energy, max: 10, color: 'text-orange-500' },
            { label: 'Anxiety', val: ci.anxiety, max: 10, color: 'text-rose-500' },
          ].map(({ label, val, max, suffix, color }) => (
            <div key={label} className="text-center">
              <p className={`text-lg font-semibold tabular-nums ${color}`}>{val}{suffix || ''}</p>
              <p className="text-[10px] text-slate-400">{label}{max ? `/${max}` : ''}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-2.5 italic leading-relaxed">"{ci.note}"</p>
      </div>
      {/* Signals */}
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <Brain size={14} className="text-slate-400" weight="duotone" />
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">What Kindra sees</span>
        </div>
        {P.signals.map((s, i) => (
          <div key={i} className={`rounded-xl border p-4 mb-2 ${s.level === 'high' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.level === 'high' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>{s.level}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">{s.cat}</span>
            </div>
            <p className="text-sm text-[#0F172A] leading-snug mb-1.5">{s.msg}</p>
            <p className="text-xs text-amber-700 font-medium">{s.action}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── PROFILE ─── */
const ProfileView = () => (
  <div className="space-y-5" data-testid="demo-profile">
    <div>
      <h1 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Health Profile</h1>
      <p className="text-xs text-slate-400 mt-0.5">{P.conditions.length} conditions · {P.medications.length} medications · {P.symptoms.length} tracked symptoms</p>
    </div>
    {/* Conditions */}
    <Section icon={Heart} iconColor="text-[#235347]" label="Your conditions">
      {P.conditions.map((c, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-3.5 mb-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#0F172A]">{c.name}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase ${c.severity === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-700'}`}>{c.severity}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Since {c.since}</p>
        </div>
      ))}
    </Section>
    {/* Medications */}
    <Section icon={Pill} iconColor="text-blue-500" label="Your medications">
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {P.medications.map((m, i) => (
          <div key={i} className="px-3.5 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#0F172A]">{m.name}</p>
              <span className="text-[11px] text-slate-400">{m.dose}</span>
            </div>
            <p className="text-[11px] text-slate-400">{m.when} · {m.purpose}</p>
            {m.prescriber && <p className="text-[10px] text-slate-300 mt-0.5">{m.prescriber}</p>}
          </div>
        ))}
      </div>
    </Section>
    {/* Symptoms */}
    <Section icon={Heartbeat} iconColor="text-rose-500" label="Your symptoms">
      <div className="space-y-1.5">
        {P.symptoms.map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 flex items-center justify-between">
            <div>
              <span className="text-sm text-[#0F172A]">{s.name}</span>
              <span className="text-[10px] text-slate-400 ml-2">{s.freq}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-rose-400 rounded-full" style={{ width: `${s.score * 10}%` }} /></div>
              <span className="text-[11px] font-semibold text-rose-500 tabular-nums w-8 text-right">{s.score}/10</span>
            </div>
          </div>
        ))}
      </div>
    </Section>
    {/* Recent check-ins */}
    <Section icon={Clock} iconColor="text-slate-400" label="Recent check-ins">
      <div className="space-y-1.5">
        {P.checkins.slice(0, 4).map((c, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-500">{c.day}</span>
              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span>Sleep {c.sleep}h</span><span>Energy {c.energy}/10</span><span>Mood {c.mood}/10</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 italic">"{c.note}"</p>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

const Section = ({ icon: Icon, iconColor, label, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2.5">
      <Icon size={15} className={iconColor} weight="duotone" />
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
    </div>
    {children}
  </div>
);

/* ─── COURSE ─── */
const CourseView = () => {
  const c = P.course;
  return (
    <div className="space-y-4" data-testid="demo-course">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#235347] mb-1">Your active course</p>
        <h2 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</h2>
        <p className="text-xs text-slate-400 mt-0.5">Step {c.step + 1} of {c.total}</p>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#235347] rounded-full transition-all" style={{ width: `${((c.step + 1) / c.total) * 100}%` }} /></div>
      {['What this is', 'Why this matters for you'].map((h, i) => (
        <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white border border-slate-100">
          <CheckCircle size={14} className="text-[#235347]" weight="fill" />
          <span className="text-[11px] text-slate-500">{h}</span>
        </div>
      ))}
      <div className="mb-2">
        <p className="text-xs text-slate-400 mb-2.5">Based on your fatigue patterns and recent check-ins</p>
        <div className="bg-amber-50/50 border border-amber-200/60 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1"><Eye size={15} className="text-amber-500" weight="duotone" /><h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">{c.currentSection.heading}</h3></div>
          <p className="text-[13px] text-slate-600 leading-relaxed mt-3">{c.currentSection.content}</p>
          <p className="text-[11px] text-slate-400 mt-4">Based on your data</p>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Why this matters</p>
        <p className="text-xs text-slate-600 leading-relaxed">Knowing what to look for means catching things early — before a bad week becomes a bad month.</p>
      </div>
      <div className="bg-white border border-slate-100 rounded-xl px-4 py-3">
        <p className="text-[11px] text-slate-400 mb-2">Was this helpful?</p>
        <div className="flex gap-2">
          {['Yes', 'No', 'Not sure'].map(l => (
            <button key={l} className="flex-1 py-2 rounded-lg text-xs font-medium border border-slate-200 text-slate-400 hover:bg-slate-50 min-h-[36px]">{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── CHAT ─── */
const ChatView = () => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const scroll = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);

  const send = useCallback(async (text) => {
    const m = (text || input).trim();
    if (!m || sending) return;
    setInput('');
    setSending(true);
    setMsgs(prev => [...prev, { role: 'user', text: m }]);
    scroll();
    try {
      const res = await fetch(`${API}/demo/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: m }) });
      const d = await res.json();
      setMsgs(prev => [...prev, { role: 'ai', text: d.content }]);
    } catch {
      setMsgs(prev => [...prev, { role: 'ai', text: 'Try asking about your fatigue, sleep, medications, or upcoming appointment with Dr. Patel.' }]);
    } finally { setSending(false); scroll(); }
  }, [input, sending]);

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 120px)' }} data-testid="demo-chat">
      <div className="flex-1 overflow-y-auto py-2">
        {msgs.length === 0 && (
          <div className="mb-4">
            <p className="text-sm text-slate-500 mb-3">Ask Kindra anything about your health:</p>
            <div className="space-y-2">
              {PROMPTS.map((p, i) => (
                <button key={i} onClick={() => send(p)} className="w-full text-left bg-white border border-slate-100 rounded-xl px-3.5 py-3 hover:bg-slate-50 transition-colors" data-testid={`demo-prompt-${i}`}>
                  <span className="text-[13px] text-slate-600">{p}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
            <div className={`max-w-[88%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-[#235347] text-white rounded-br-md' : 'bg-white border border-slate-200 rounded-bl-md'}`}>
              {m.role === 'ai' ? (
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: m.text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
              ) : (
                <p className="text-sm leading-relaxed">{m.text}</p>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start mb-3"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3"><div className="flex gap-1"><div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" /><div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div></div></div>
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-slate-100 pt-2 pb-1">
        {msgs.length > 0 && (
          <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
            {PROMPTS.filter(p => !msgs.some(m => m.role === 'user' && m.text === p)).slice(0, 3).map((p, i) => (
              <button key={i} onClick={() => send(p)} className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-3 py-1.5 whitespace-nowrap hover:bg-slate-100 flex-shrink-0">{p}</button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }}
            placeholder="Ask about your health..." className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-base outline-none focus:ring-2 focus:ring-[#235347]/20" style={{ fontSize: '16px' }} data-testid="demo-chat-input" />
          <button onClick={() => send()} disabled={!input.trim() || sending} className="p-3 bg-[#235347] text-white rounded-xl hover:bg-[#1A3F36] disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="demo-chat-send">
            <PaperPlaneRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── DOCUMENTS ─── */
const DocumentsView = () => {
  const [sel, setSel] = useState(0);
  const doc = P.documents[sel];
  return (
    <div className="space-y-4" data-testid="demo-documents">
      <h1 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Documents</h1>
      <div className="flex gap-2">
        {P.documents.map((d, i) => (
          <button key={i} onClick={() => setSel(i)} className={`flex-1 text-left rounded-xl p-3 border transition-colors ${sel === i ? 'border-[#235347] bg-[#235347]/5' : 'border-slate-200 bg-white'}`} data-testid={`demo-doc-tab-${i}`}>
            <FileText size={16} className={sel === i ? 'text-[#235347]' : 'text-slate-400'} weight="duotone" />
            <p className="text-xs font-medium text-[#0F172A] mt-1.5 line-clamp-2">{d.title}</p>
            <p className="text-[10px] text-slate-400">{d.type}</p>
          </button>
        ))}
      </div>
      {/* Source & meta */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-2.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-medium">{doc.source}</p>
          <p className="text-[10px] text-slate-400">{doc.date}</p>
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5">Ordered by {doc.orderedBy}</p>
        {doc.ref && <p className="text-[10px] text-slate-300 mt-0.5">Ref: {doc.ref}</p>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">What this means for you</p>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">{doc.summary}</p>
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Key findings</p>
        <div className="space-y-2.5">
          {doc.findings.map((f, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#235347] mt-1.5 flex-shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-[#0F172A]"><strong>{f.label}:</strong> {f.value}</p>
                  {f.range && <span className="text-[10px] text-slate-300">({f.range})</span>}
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">{f.flag}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-400">This document was automatically parsed and connected to your health profile.</p>
    </div>
  );
};

/* ─── APPOINTMENTS ─── */
const AppointmentsView = ({ onNav }) => {
  const u = P.appointments.upcoming;
  const past = P.appointments.past;
  return (
    <div className="space-y-5" data-testid="demo-appointments">
      <h1 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Appointments</h1>
      {/* Upcoming */}
      <div className="bg-[#235347]/5 border border-[#235347]/15 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarBlank size={16} className="text-[#235347]" weight="duotone" />
          <span className="text-[11px] font-bold text-[#235347] uppercase tracking-wider">Upcoming</span>
        </div>
        <p className="text-base font-semibold text-[#0F172A]">{u.doctor}</p>
        <p className="text-xs text-slate-500">{u.role}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs text-slate-500 flex items-center gap-1"><CalendarBlank size={12} /> {u.date}</span>
          <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {u.time}</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">{u.location}</p>
        {/* What changed since last */}
        <div className="mt-4 pt-3 border-t border-[#235347]/10">
          <p className="text-[11px] font-semibold text-[#235347] uppercase tracking-wider mb-2">What's changed since your last visit</p>
          <div className="space-y-2">
            {['Sleep duration down from 5.5h to 4.8h average', 'Anxiety increased from 4/10 to 6-7/10', 'Night waking frequency up to 4-5 times/week', 'Brain fog affecting work on 3 of last 5 days'].map((c, i) => (
              <div key={i} className="flex items-start gap-2"><Warning size={12} className="text-amber-500 mt-0.5 flex-shrink-0" /><p className="text-xs text-slate-600">{c}</p></div>
            ))}
          </div>
        </div>
        {/* What to raise */}
        <div className="mt-4 pt-3 border-t border-[#235347]/10">
          <p className="text-[11px] font-semibold text-[#235347] uppercase tracking-wider mb-2">What to raise</p>
          <div className="space-y-2">
            {['2-3am waking pattern (sleep maintenance vs onset)', 'Sertraline evening timing — ask about morning switch', 'Whether CBT-I is appropriate for your insomnia type'].map((q, i) => (
              <div key={i} className="flex items-start gap-2"><Target size={12} className="text-[#235347] mt-0.5 flex-shrink-0" /><p className="text-xs text-slate-600">{q}</p></div>
            ))}
          </div>
        </div>
        <button onClick={() => onNav('brief')} className="w-full mt-4 bg-[#235347] text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-[#1A3F36] flex items-center justify-center gap-2 min-h-[44px]" data-testid="demo-view-brief">
          <Clipboard size={16} /> View full appointment prep
        </button>
      </div>
      {/* Past */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2"><CheckCircle size={14} className="text-slate-400" weight="fill" /><span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Past</span></div>
        <p className="text-sm font-medium text-[#0F172A]">{past.doctor}</p>
        <p className="text-[11px] text-slate-400">{past.role} · {past.date}</p>
        {past.location && <p className="text-[10px] text-slate-300 mt-0.5">{past.location}</p>}
        <p className="text-xs text-slate-500 mt-1.5">{past.notes}</p>
      </div>
      {/* Referral */}
      {P.appointments.referral && (
        <div className="bg-blue-50/50 border border-blue-200/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><File size={14} className="text-blue-500" weight="duotone" /><span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider">Referral</span></div>
          <p className="text-xs text-slate-600 leading-relaxed"><strong>{P.appointments.referral.from}</strong> &rarr; <strong>{P.appointments.referral.to}</strong></p>
          <p className="text-[10px] text-slate-400 mt-0.5">{P.appointments.referral.date}</p>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">{P.appointments.referral.reason}</p>
        </div>
      )}
    </div>
  );
};

/* ─── DOCTOR BRIEF ─── */
const BriefView = ({ onNav }) => {
  const b = P.doctorBrief;
  const u = P.appointments.upcoming;
  return (
    <div className="space-y-4" data-testid="demo-brief">
      <div>
        <button onClick={() => onNav('appointments')} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 mb-2"><ArrowLeft size={14} /> Back to appointments</button>
        <h1 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Appointment Prep</h1>
        <p className="text-xs text-slate-400 mt-0.5">{u.doctor} · {u.date}</p>
      </div>
      {/* Key data strip */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Sleep avg', val: b.keyData.sleepAvg, color: 'bg-blue-50 text-blue-700' },
          { label: 'Energy avg', val: b.keyData.energyAvg, color: 'bg-orange-50 text-orange-700' },
          { label: 'Anxiety avg', val: b.keyData.anxietyAvg, color: 'bg-rose-50 text-rose-600' },
          { label: 'Wake-ups', val: b.keyData.wakings, color: 'bg-amber-50 text-amber-700' },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${color} rounded-lg px-2.5 py-2 text-center`}>
            <p className="text-sm font-semibold tabular-nums">{val}</p>
            <p className="text-[9px] uppercase tracking-wider font-medium opacity-70">{label}</p>
          </div>
        ))}
      </div>
      {/* Current concerns */}
      <BriefSection icon={Warning} iconColor="text-red-500" label="Current concerns">
        {b.concerns.map((c, i) => <BulletItem key={i} color="bg-red-400">{c}</BulletItem>)}
      </BriefSection>
      {/* Recent changes */}
      <BriefSection icon={Lightning} iconColor="text-amber-500" label="Recent changes">
        {b.changes.map((c, i) => <BulletItem key={i} color="bg-amber-400">{c}</BulletItem>)}
      </BriefSection>
      {/* Medication context */}
      <BriefSection icon={Pill} iconColor="text-blue-500" label="Medication context">
        {b.medications.map((m, i) => <BulletItem key={i} color="bg-blue-400">{m}</BulletItem>)}
      </BriefSection>
      {/* Questions to ask */}
      <BriefSection icon={Stethoscope} iconColor="text-[#235347]" label="Suggested questions for your doctor">
        {b.questions.map((q, i) => (
          <div key={i} className="flex items-start gap-2.5 mb-2">
            <span className="text-sm text-[#235347] font-semibold mt-0.5 flex-shrink-0">{i + 1}.</span>
            <p className="text-sm text-slate-700 leading-relaxed">{q}</p>
          </div>
        ))}
      </BriefSection>
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        {P.doctorBrief.referralContext && <p className="text-[11px] text-slate-500 leading-relaxed mb-2">{P.doctorBrief.referralContext}</p>}
        <p className="text-[11px] text-slate-400 leading-relaxed">This summary was generated from your check-ins, medications, documents, and tracked patterns over the past 30 days.</p>
      </div>
    </div>
  );
};

const BriefSection = ({ icon: Icon, iconColor, label, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2.5"><Icon size={14} className={iconColor} weight="duotone" /><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span></div>
    {children}
  </div>
);
const BulletItem = ({ color, children }) => (
  <div className="flex items-start gap-2.5 mb-2"><span className={`w-1.5 h-1.5 rounded-full ${color} mt-1.5 flex-shrink-0`} /><p className="text-sm text-slate-700 leading-relaxed">{children}</p></div>
);

/* ═══════════════════════════════════════════════════
   FINAL CTA SCREEN
   ═══════════════════════════════════════════════════ */
const CtaScreen = ({ onStartFree, onExploreDemo, onReplay }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
    style={{
      paddingTop: 'max(env(safe-area-inset-top, 0px), 1rem)',
      paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)',
    }}
    data-testid="demo-cta-screen"
  >
    <div className="max-w-md w-full text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#235347]/5 mb-6">
        <Sparkle size={28} className="text-[#235347]" weight="duotone" />
      </div>
      <h1
        className="text-2xl sm:text-3xl font-semibold text-[#0F172A] leading-tight mb-3"
        style={{ fontFamily: 'Outfit, sans-serif' }}
        data-testid="demo-cta-headline"
      >
        See how Kindra helps over time
      </h1>
      <p className="text-sm text-slate-500 leading-relaxed mb-8 max-w-xs mx-auto">
        Use the guided demo, or step into a fully populated sample account.
      </p>
      <div className="space-y-3">
        <button
          onClick={onStartFree}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#235347] text-white rounded-xl px-6 py-4 text-base font-medium hover:bg-[#1A3F36] transition-colors min-h-[52px]"
          data-testid="demo-cta-start-free"
        >
          Start Free <ArrowRight size={16} weight="bold" />
        </button>
        <button
          onClick={onExploreDemo}
          className="w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-[#0F172A] rounded-xl px-6 py-4 text-base font-medium hover:bg-slate-50 transition-colors min-h-[52px]"
          data-testid="demo-cta-explore-demo"
        >
          <UserCircle size={18} weight="duotone" /> Explore Demo Account
        </button>
      </div>
      <button
        onClick={onReplay}
        className="mt-6 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#235347] transition-colors"
        data-testid="demo-cta-replay"
      >
        <ArrowClockwise size={12} /> Replay the tour
      </button>
    </div>
  </motion.div>
);

/* ═══════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════ */
const DemoPage = () => {
  const navigate = useNavigate();
  // phase: 'intro' | 'tour' | 'cta'
  const [phase, setPhase] = useState('intro');
  const [view, setView] = useState('dashboard');
  const [gStep, setGStep] = useState(0);
  const [guideOn, setGuideOn] = useState(true);
  const [paused, setPaused] = useState(false);
  const autoPlay = true; // auto-advance ON by default

  // ─── Auto-advance effect ───
  useEffect(() => {
    if (phase !== 'tour' || !guideOn || paused) return;
    const t = setTimeout(() => {
      if (gStep < STEPS.length - 1) {
        const n = gStep + 1;
        setGStep(n);
        setView(STEPS[n].view);
      } else {
        setPhase('cta');
      }
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [phase, gStep, guideOn, paused]);

  // ─── Analytics: fire step_viewed on every tour step ───
  useEffect(() => {
    if (phase === 'tour') {
      track('demo_step_viewed', {
        step: gStep,
        step_id: STEPS[gStep]?.id,
        view: STEPS[gStep]?.view,
      });
    }
  }, [phase, gStep]);

  // ─── Analytics: demo_completed + demo_cta_screen_viewed ───
  useEffect(() => {
    if (phase === 'cta') {
      track('demo_completed');
      track('demo_cta_screen_viewed');
    }
  }, [phase]);

  const start = () => {
    track('demo_started');
    setPhase('tour');
    setView(STEPS[0].view);
    setGStep(0);
    setGuideOn(true);
    setPaused(false);
  };

  const replay = () => {
    track('demo_started', { replay: true });
    setPhase('tour');
    setView(STEPS[0].view);
    setGStep(0);
    setGuideOn(true);
    setPaused(false);
  };

  const restart = () => {
    setPhase('intro');
    setView('dashboard');
    setGStep(0);
    setGuideOn(true);
    setPaused(false);
  };

  const gNext = () => {
    setPaused(true); // user interaction pauses autoplay
    if (gStep < STEPS.length - 1) {
      const n = gStep + 1;
      setGStep(n);
      setView(STEPS[n].view);
    } else {
      setPhase('cta');
    }
  };
  const gPrev = () => {
    setPaused(true);
    if (gStep > 0) {
      const n = gStep - 1;
      setGStep(n);
      setView(STEPS[n].view);
    }
  };
  const skipTour = () => {
    setPaused(true);
    setPhase('cta');
  };

  const togglePause = () => setPaused((p) => !p);

  // Manual nav click / view interaction → pause autoplay but stay in tour
  const handleNav = (v) => {
    setPaused(true);
    setView(v);
  };

  const handleStartFree = () => {
    track('clicked_start_free');
    navigate('/register');
  };
  const handleExploreDemo = () => {
    track('clicked_demo_account');
    navigate('/login', {
      state: {
        prefillEmail: DEMO_ACCOUNT.email,
        prefillPassword: DEMO_ACCOUNT.password,
        demoBanner: true,
      },
    });
  };

  // ─── Intro screen ───
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="demo-start-screen">
        <div className="max-w-md w-full text-center">
          <img src="/kindra-logo.png" alt="Kindra" className="h-28 mx-auto object-contain mb-6" />
          <p className="text-base text-[#235347] font-medium mb-1">Care between appointments.</p>
          <p className="text-xs text-slate-400 leading-relaxed mb-8 max-w-xs mx-auto">
            Helping you understand what's happening with your health — and what to do next. Step into a realistic health profile to see how it works.
          </p>
          <button onClick={start} className="inline-flex items-center gap-2.5 bg-[#235347] text-white rounded-xl px-8 py-3.5 text-sm font-medium hover:bg-[#1A3F36] min-h-[48px]" data-testid="demo-start-btn">
            <Play size={16} weight="fill" /> Start Demo
          </button>
          <p className="text-[10px] text-slate-300 mt-6">Isolated demo environment — no real data is accessed.</p>
        </div>
      </div>
    );
  }

  // ─── Final CTA screen ───
  if (phase === 'cta') {
    return <CtaScreen onStartFree={handleStartFree} onExploreDemo={handleExploreDemo} onReplay={replay} />;
  }

  // ─── Tour view ───
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="demo-active">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-lg mx-auto px-4 py-2.5 flex items-center justify-between min-h-[48px]">
          <div className="flex items-center gap-2.5">
            <img src="/icon-192x192.png" alt="Kindra" className="w-7 h-7 rounded-lg object-contain" />
            <span className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Kindra</span>
          </div>
          <div className="flex items-center gap-2">
            {!guideOn && <button onClick={() => { setGStep(0); setGuideOn(true); setView('profile'); setPaused(false); }} className="text-[11px] text-[#235347] font-medium hover:underline" data-testid="demo-restart-tour">Restart tour</button>}
            <button onClick={restart} className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid="demo-restart-btn"><ArrowClockwise size={12} /> Reset</button>
          </div>
        </div>
      </header>
      <div className="max-w-lg mx-auto px-4 py-4" style={{ paddingBottom: '80px' }}>
        <AnimatePresence mode="wait">
          <motion.div key={view} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {view === 'dashboard' && <DashboardView onNav={handleNav} />}
            {view === 'profile' && <ProfileView />}
            {view === 'course' && <CourseView />}
            {view === 'chat' && <ChatView />}
            {view === 'documents' && <DocumentsView />}
            {view === 'appointments' && <AppointmentsView onNav={handleNav} />}
            {view === 'brief' && <BriefView onNav={handleNav} />}
          </motion.div>
        </AnimatePresence>
      </div>
      <Nav active={view === 'brief' ? 'appointments' : view} onNav={handleNav} />
      <AnimatePresence>
        {guideOn && (
          <Overlay
            step={gStep}
            onNext={gNext}
            onPrev={gPrev}
            onSkip={skipTour}
            autoPlay={autoPlay}
            paused={paused}
            onTogglePause={togglePause}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DemoPage;
